/**
 * Rhino-1 Challenges Page
 * Onboarding quests and platform mastery challenges
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Rocket, Trophy, Star, Target, CheckCircle2, Circle, Lock,
  ArrowLeft, Award, Zap, TrendingUp, Users, Clock, Flame,
  Crown, Medal, ChevronRight, Sparkles, Gift
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Difficulty configurations
const DIFFICULTY_CONFIG = {
  STARTER: { 
    label: 'Starter', 
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: Zap,
    color: 'text-green-500'
  },
  INTERMEDIATE: { 
    label: 'Intermediate', 
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    icon: TrendingUp,
    color: 'text-amber-500'
  },
  ADVANCED: { 
    label: 'Advanced', 
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: Flame,
    color: 'text-red-500'
  }
};

// Status configurations
const STATUS_CONFIG = {
  NOT_STARTED: { label: 'Not Started', icon: Circle, color: 'text-slate-400' },
  IN_PROGRESS: { label: 'In Progress', icon: Zap, color: 'text-blue-500' },
  COMPLETED: { label: 'Completed', icon: CheckCircle2, color: 'text-green-500' }
};

// Challenge Card Component
const ChallengeCard = ({ challenge, onClick }) => {
  const diffConfig = DIFFICULTY_CONFIG[challenge.difficulty] || DIFFICULTY_CONFIG.EASY;
  const statusConfig = STATUS_CONFIG[challenge.status] || STATUS_CONFIG.NOT_STARTED;
  const StatusIcon = statusConfig.icon;
  const DiffIcon = diffConfig.icon;
  
  const isCompleted = challenge.status === 'COMPLETED';
  const isInProgress = challenge.status === 'IN_PROGRESS';
  
  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-lg ${
        isCompleted 
          ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800' 
          : isInProgress
            ? 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800'
            : 'hover:border-slate-300 dark:hover:border-slate-600'
      }`}
      onClick={onClick}
      data-testid={`challenge-card-${challenge.id}`}
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${
              isCompleted 
                ? 'bg-green-100 dark:bg-green-900/50' 
                : isInProgress
                  ? 'bg-blue-100 dark:bg-blue-900/50'
                  : 'bg-slate-100 dark:bg-slate-800'
            }`}>
              {challenge.badge_icon ? (
                <span className="text-2xl">{challenge.badge_icon}</span>
              ) : (
                <Rocket className={`h-6 w-6 ${isCompleted ? 'text-green-600' : isInProgress ? 'text-blue-600' : 'text-slate-500'}`} />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-lg">{challenge.title}</h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={diffConfig.className}>
                  <DiffIcon className="h-3 w-3 mr-1" />
                  {diffConfig.label}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  <Star className="h-3 w-3 mr-1 text-amber-500" />
                  {challenge.points_reward} pts
                </Badge>
              </div>
            </div>
          </div>
          <StatusIcon className={`h-6 w-6 ${statusConfig.color}`} />
        </div>

        {/* Description */}
        {challenge.description && (
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {challenge.description}
          </p>
        )}

        {/* Requirements Preview */}
        {challenge.requirements?.length > 0 && (
          <div className="space-y-2 mb-4">
            {challenge.requirements.slice(0, 3).map((req, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                {req.is_completed ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                ) : (
                  <Circle className="h-4 w-4 text-slate-300 shrink-0" />
                )}
                <span className={req.is_completed ? 'text-muted-foreground line-through' : ''}>
                  {req.label}
                </span>
                {req.target_count > 1 && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    {req.current_count || 0}/{req.target_count}
                  </span>
                )}
              </div>
            ))}
            {challenge.requirements.length > 3 && (
              <p className="text-xs text-muted-foreground">
                +{challenge.requirements.length - 3} more requirements
              </p>
            )}
          </div>
        )}

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{Math.round(challenge.progress_percent || 0)}%</span>
          </div>
          <Progress 
            value={challenge.progress_percent || 0} 
            className={`h-2 ${isCompleted ? '[&>div]:bg-green-500' : isInProgress ? '[&>div]:bg-blue-500' : ''}`}
          />
        </div>

        {/* Completed Badge */}
        {isCompleted && challenge.completed_at && (
          <div className="mt-4 pt-3 border-t flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <Award className="h-4 w-4" />
            Completed {new Date(challenge.completed_at).toLocaleDateString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Leaderboard Entry Component
const LeaderboardEntry = ({ entry, isCurrentUser }) => {
  const getRankDisplay = (rank) => {
    if (rank === 1) return <Crown className="h-5 w-5 text-amber-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-slate-400" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-700" />;
    return <span className="font-bold">{rank}</span>;
  };

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${
      isCurrentUser ? 'bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800' : 'hover:bg-accent/50'
    }`}>
      <div className="w-8 flex justify-center">{getRankDisplay(entry.rank)}</div>
      <Avatar className="h-8 w-8">
        <AvatarFallback className="text-sm">{entry.user_name?.charAt(0)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{entry.user_name}</p>
        <p className="text-xs text-muted-foreground">
          {entry.challenges_completed} challenges • {entry.badges_earned} badges
        </p>
      </div>
      <div className="text-right">
        <p className="font-bold text-amber-600 dark:text-amber-400">{entry.total_points}</p>
        <p className="text-xs text-muted-foreground">points</p>
      </div>
    </div>
  );
};

// Badge Display Component
const BadgeDisplay = ({ badge }) => {
  return (
    <div className="flex flex-col items-center p-3 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200 dark:border-amber-800">
      <span className="text-3xl mb-2">{badge.badge_icon || '🏆'}</span>
      <p className="text-sm font-medium text-center">{badge.badge_name}</p>
      <p className="text-xs text-muted-foreground">
        {new Date(badge.earned_at).toLocaleDateString()}
      </p>
    </div>
  );
};

// Main Component
export const RhinoChallenges = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [challenges, setChallenges] = useState([]);
  const [widgetData, setWidgetData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userBadges, setUserBadges] = useState({ badges: [], total_points: 0 });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [challengesRes, widgetRes, leaderboardRes, badgesRes] = await Promise.all([
        axios.get(`${API}/rhino-challenges?user_id=${user?.id}`),
        axios.get(`${API}/rhino-challenges/widget/data?user_id=${user?.id}`),
        axios.get(`${API}/rhino-challenges/leaderboard?user_id=${user?.id}&period=all`),
        axios.get(`${API}/rhino-challenges/user/badges?user_id=${user?.id}`)
      ]);
      
      setChallenges(challengesRes.data || []);
      setWidgetData(widgetRes.data);
      setLeaderboard(leaderboardRes.data?.entries || []);
      setUserBadges(badgesRes.data || { badges: [], total_points: 0 });
    } catch (error) {
      console.error('Error fetching challenges data:', error);
      toast.error('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
  }, [fetchData, user?.id]);

  const handleChallengeClick = (challenge) => {
    // Could navigate to detail page or show modal
    toast.info(`Viewing: ${challenge.title}`);
  };

  // Filter challenges by tab
  const filteredChallenges = challenges.filter(ch => {
    if (activeTab === 'all') return true;
    if (activeTab === 'in-progress') return ch.status === 'IN_PROGRESS';
    if (activeTab === 'completed') return ch.status === 'COMPLETED';
    if (activeTab === 'starter') return ch.difficulty === 'STARTER';
    return true;
  });

  // Group challenges by difficulty for the "all" view
  const groupedChallenges = {
    STARTER: challenges.filter(c => c.difficulty === 'STARTER'),
    INTERMEDIATE: challenges.filter(c => c.difficulty === 'INTERMEDIATE'),
    ADVANCED: challenges.filter(c => c.difficulty === 'ADVANCED')
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-64 bg-slate-200 dark:bg-slate-800 rounded" />
            <div className="grid grid-cols-4 gap-4">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-24 bg-slate-200 dark:bg-slate-800 rounded-lg" />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="h-48 bg-slate-200 dark:bg-slate-800 rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="rhino-challenges-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/competition')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Rocket className="h-7 w-7 text-blue-500" />
              Rhino-1 Challenges
            </h1>
            <p className="text-muted-foreground">
              Complete onboarding quests to master the platform and earn rewards
            </p>
          </div>
          <Button 
            variant="outline"
            onClick={() => navigate('/competition/leaderboards')}
          >
            <Trophy className="h-4 w-4 mr-2" />
            Leaderboards
          </Button>
        </div>

        {/* Stats Overview */}
        {widgetData && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Target className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{widgetData.total_challenges}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{widgetData.completed_challenges}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Zap className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{widgetData.in_progress_challenges}</p>
                  <p className="text-xs text-muted-foreground">In Progress</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <Star className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{widgetData.total_points}</p>
                  <p className="text-xs text-muted-foreground">Points</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                  <Award className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{widgetData.total_badges}</p>
                  <p className="text-xs text-muted-foreground">Badges</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Overall Progress */}
        {widgetData && (
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-blue-500" />
                    Platform Mastery
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {widgetData.completed_challenges} of {widgetData.total_challenges} challenges completed
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                    {Math.round(widgetData.completion_percent || 0)}%
                  </p>
                  <p className="text-xs text-muted-foreground">Complete</p>
                </div>
              </div>
              <Progress value={widgetData.completion_percent || 0} className="h-3 [&>div]:bg-blue-500" />
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Challenges List */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All ({challenges.length})</TabsTrigger>
                <TabsTrigger value="in-progress">
                  In Progress ({challenges.filter(c => c.status === 'IN_PROGRESS').length})
                </TabsTrigger>
                <TabsTrigger value="completed">
                  Completed ({challenges.filter(c => c.status === 'COMPLETED').length})
                </TabsTrigger>
                <TabsTrigger value="starter">
                  Get Started ({groupedChallenges.STARTER.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="mt-4 space-y-6">
                {Object.entries(DIFFICULTY_CONFIG).map(([difficulty, config]) => {
                  const group = groupedChallenges[difficulty];
                  if (!group || group.length === 0) return null;
                  
                  const DiffIcon = config.icon;
                  return (
                    <div key={difficulty}>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <DiffIcon className={`h-5 w-5 ${config.color}`} />
                        {config.label} Challenges
                        <Badge variant="outline" className="ml-2">
                          {group.filter(c => c.status === 'COMPLETED').length}/{group.length}
                        </Badge>
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {group.map(challenge => (
                          <ChallengeCard
                            key={challenge.id}
                            challenge={challenge}
                            onClick={() => handleChallengeClick(challenge)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </TabsContent>

              <TabsContent value="in-progress" className="mt-4">
                {filteredChallenges.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Zap className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <h3 className="font-semibold mb-2">No challenges in progress</h3>
                      <p className="text-muted-foreground mb-4">
                        Start working on a challenge to see it here!
                      </p>
                      <Button onClick={() => setActiveTab('starter')}>
                        View Starter Challenges
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredChallenges.map(challenge => (
                      <ChallengeCard
                        key={challenge.id}
                        challenge={challenge}
                        onClick={() => handleChallengeClick(challenge)}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="completed" className="mt-4">
                {filteredChallenges.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Trophy className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <h3 className="font-semibold mb-2">No completed challenges yet</h3>
                      <p className="text-muted-foreground">
                        Complete challenges to earn points and badges!
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredChallenges.map(challenge => (
                      <ChallengeCard
                        key={challenge.id}
                        challenge={challenge}
                        onClick={() => handleChallengeClick(challenge)}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="starter" className="mt-4">
                <Card className="mb-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <Gift className="h-6 w-6 text-green-600" />
                      <div>
                        <p className="font-medium">New to the platform?</p>
                        <p className="text-sm text-muted-foreground">
                          Start with these beginner-friendly challenges to learn the basics
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {groupedChallenges.STARTER.map(challenge => (
                    <ChallengeCard
                      key={challenge.id}
                      challenge={challenge}
                      onClick={() => handleChallengeClick(challenge)}
                    />
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Points Leaderboard */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  Points Leaderboard
                </CardTitle>
                <CardDescription>Top challengers</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {leaderboard.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No rankings yet. Be the first!
                  </p>
                ) : (
                  leaderboard.slice(0, 5).map(entry => (
                    <LeaderboardEntry 
                      key={entry.user_id} 
                      entry={entry}
                      isCurrentUser={entry.user_id === user?.id}
                    />
                  ))
                )}
                <Button 
                  variant="ghost" 
                  className="w-full mt-2"
                  onClick={() => navigate('/competition/leaderboards')}
                >
                  View Full Leaderboard
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </CardContent>
            </Card>

            {/* Your Badges */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="h-5 w-5 text-pink-500" />
                  Your Badges
                </CardTitle>
                <CardDescription>
                  {userBadges.badges?.length || 0} earned
                </CardDescription>
              </CardHeader>
              <CardContent>
                {userBadges.badges?.length === 0 ? (
                  <div className="text-center py-6">
                    <Award className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      Complete challenges to earn badges!
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {userBadges.badges.slice(0, 6).map(badge => (
                      <BadgeDisplay key={badge.id} badge={badge} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Completions Feed */}
            {widgetData?.recent_completions?.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Flame className="h-5 w-5 text-orange-500" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {widgetData.recent_completions.slice(0, 5).map(completion => (
                    <div key={completion.id} className="flex items-center gap-3 text-sm">
                      <Avatar className="h-7 w-7">
                        <AvatarFallback className="text-xs">{completion.user_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="truncate">
                          <span className="font-medium">{completion.user_name}</span>
                          {' '}completed{' '}
                          <span className="text-blue-600 dark:text-blue-400">{completion.challenge_title}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          +{completion.points_earned} pts
                        </p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RhinoChallenges;
